import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import '../../sass/pages/ssl-certificates.scss';

type SSLView = 'manage' | 'display' | 'run';
type AlertType = 'success' | 'danger' | 'info' | 'warning';

interface SSLCertificate {
  id?: number;
  name: string;
  environment: string;
  commonName: string;
  checkType: 'url' | 'manual';
  url: string;
  expiresAt: string | null;
  issuer: string;
  subject: string;
  serialNumber: string;
  csr: string;
  notes: string;
  notificationEmails: string;
  notifyDaysBefore: number;
  lastCheckedAt?: string | null;
  lastCheckStatus?: string;
  lastCheckError?: string;
  isActive: boolean;
  daysUntilExpiry?: number | null;
  status?: string;
}

interface CheckSummary {
  checked: number;
  successful: number;
  failed: number;
  manual: number;
  expiringSoon: number;
  expired: number;
  emailsSent: number;
  results: Array<{
    id: number;
    name: string;
    status: string;
    message: string;
    daysUntilExpiry?: number;
  }>;
}

interface DisplayField {
  key: keyof SSLCertificate | 'daysUntilExpiry' | 'status';
  label: string;
}

const emptyCertificate: SSLCertificate = {
  name: '',
  environment: '',
  commonName: '',
  checkType: 'url',
  url: '',
  expiresAt: null,
  issuer: '',
  subject: '',
  serialNumber: '',
  csr: '',
  notes: '',
  notificationEmails: '',
  notifyDaysBefore: 30,
  isActive: true,
};

const displayFields: DisplayField[] = [
  { key: 'name', label: 'Name' },
  { key: 'environment', label: 'Environment' },
  { key: 'commonName', label: 'Common Name' },
  { key: 'expiresAt', label: 'Expiry Date' },
  { key: 'url', label: 'URL' },
  { key: 'checkType', label: 'Certificate Type' },
  { key: 'issuer', label: 'Issuer' },
  { key: 'subject', label: 'Subject' },
  { key: 'daysUntilExpiry', label: 'Days Until Expiry' },
  { key: 'lastCheckedAt', label: 'Last Checked At' },
  { key: 'lastCheckStatus', label: 'Last Check Status' },
  { key: 'notes', label: 'Notes' },
  { key: 'status', label: 'Status' },
];

const defaultVisibleFields = ['name', 'environment', 'daysUntilExpiry', 'status'];

const toDateInputValue = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }
  return value.slice(0, 10);
};

const fromDateInputValue = (value: string) => {
  if (!value) {
    return null;
  }
  return `${value}T16:00:00Z`;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString();
};

const getStatusVariant = (status: string | undefined) => {
  switch (status) {
    case 'Valid':
      return 'success';
    case 'Expiring Soon':
      return 'warning';
    case 'Expired':
    case 'Check Failed':
      return 'danger';
    default:
      return 'secondary';
  }
};

const SSLCertificates: React.FC = () => {
  const [activeView, setActiveView] = useState<SSLView>('display');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [certificates, setCertificates] = useState<SSLCertificate[]>([]);
  const [selectedCertificateId, setSelectedCertificateId] = useState<number | null>(null);
  const [formData, setFormData] = useState<SSLCertificate>(emptyCertificate);
  const [visibleFields, setVisibleFields] = useState<string[]>(defaultVisibleFields);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<AlertType>('info');
  const [loading, setLoading] = useState(false);
  const [runSummary, setRunSummary] = useState<CheckSummary | null>(null);
  const [searchName, setSearchName] = useState('');
  const [filterEnv, setFilterEnv] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      const response = await __request(OpenAPI, {
        method: 'GET',
        url: '/api/apitest/ssl-certificates/',
      }) as SSLCertificate[];
      setCertificates(response);
    } catch (error: unknown) {
      setStatusMessage(`Error loading SSL certificates: ${String(error)}`);
      setStatusType('danger');
    }
  };

  const sortedCertificates = useMemo(() => {
    return [...certificates].sort((left, right) => {
      const leftTime = left.expiresAt ? new Date(left.expiresAt).getTime() : Number.MAX_SAFE_INTEGER;
      const rightTime = right.expiresAt ? new Date(right.expiresAt).getTime() : Number.MAX_SAFE_INTEGER;
      return sortDirection === 'asc' ? leftTime - rightTime : rightTime - leftTime;
    });
  }, [certificates, sortDirection]);

  const uniqueEnvironments = useMemo(() => {
    return [...new Set(certificates.map((c: SSLCertificate) => c.environment).filter(Boolean))].sort();
  }, [certificates]);

  const filteredCertificates = useMemo(() => {
    return sortedCertificates.filter((cert: SSLCertificate) => {
      const nameMatch = !searchName || cert.name.toLowerCase().includes(searchName.toLowerCase());
      const envMatch = !filterEnv || cert.environment === filterEnv;
      const statusMatch = !filterStatus || (cert.status || 'Unknown') === filterStatus;
      return nameMatch && envMatch && statusMatch;
    });
  }, [sortedCertificates, searchName, filterEnv, filterStatus]);

  const updateFormField = <K extends keyof SSLCertificate>(field: K, value: SSLCertificate[K]) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const resetForm = () => {
    setSelectedCertificateId(null);
    setFormData(emptyCertificate);
  };

  const editCertificate = (certificate: SSLCertificate) => {
    setSelectedCertificateId(certificate.id ?? null);
    setFormData({
      ...emptyCertificate,
      ...certificate,
    });
    setActiveView('manage');
  };

  const saveCertificate = async () => {
    if (!formData.name.trim()) {
      setStatusMessage('Certificate name is required.');
      setStatusType('danger');
      return;
    }

    if (formData.checkType === 'url' && !formData.url.trim()) {
      setStatusMessage('URL is required for URL checked certificates.');
      setStatusType('danger');
      return;
    }

    setLoading(true);
    try {
      const body = {
        ...formData,
        expiresAt: formData.expiresAt,
      };

      const response = await __request(OpenAPI, {
        method: selectedCertificateId ? 'PATCH' : 'POST',
        url: selectedCertificateId
          ? `/api/apitest/ssl-certificates/${selectedCertificateId}/`
          : '/api/apitest/ssl-certificates/',
        body,
      }) as SSLCertificate;

      setStatusMessage(`SSL certificate ${selectedCertificateId ? 'updated' : 'created'} successfully.`);
      setStatusType('success');
      setSelectedCertificateId(response.id ?? null);
      setFormData({ ...emptyCertificate, ...response });
      await loadCertificates();
    } catch (error: unknown) {
      setStatusMessage(`Error saving SSL certificate: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const deleteCertificate = async (certificate: SSLCertificate) => {
    if (!certificate.id) {
      return;
    }

    setLoading(true);
    try {
      await __request(OpenAPI, {
        method: 'DELETE',
        url: `/api/apitest/ssl-certificates/${certificate.id}/`,
      });
      setStatusMessage('SSL certificate deleted successfully.');
      setStatusType('success');
      if (selectedCertificateId === certificate.id) {
        resetForm();
      }
      await loadCertificates();
    } catch (error: unknown) {
      setStatusMessage(`Error deleting SSL certificate: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const checkCertificateNow = async (certificate: SSLCertificate) => {
    if (!certificate.id) {
      return;
    }

    setLoading(true);
    try {
      const summary = await __request(OpenAPI, {
        method: 'POST',
        url: `/api/apitest/ssl-certificates/${certificate.id}/check-now/`,
      }) as CheckSummary;
      setRunSummary(summary);
      setStatusMessage('SSL certificate check completed.');
      setStatusType(summary.failed > 0 ? 'warning' : 'success');
      await loadCertificates();
    } catch (error: unknown) {
      setStatusMessage(`Error checking SSL certificate: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const runExpiryCheck = async () => {
    setLoading(true);
    try {
      const summary = await __request(OpenAPI, {
        method: 'POST',
        url: '/api/apitest/ssl-certificates/run-expiry-check/',
      }) as CheckSummary;
      setRunSummary(summary);
      setStatusMessage('SSL certificate expiry check completed.');
      setStatusType(summary.failed > 0 ? 'warning' : 'success');
      await loadCertificates();
    } catch (error: unknown) {
      setStatusMessage(`Error running SSL certificate expiry check: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (field: string) => {
    setVisibleFields((previous) => {
      if (previous.includes(field)) {
        return previous.filter((item) => item !== field);
      }
      return [...previous, field];
    });
  };

  const renderFieldValue = (certificate: SSLCertificate, field: string) => {
    const value = certificate[field as keyof SSLCertificate];

    if (field === 'expiresAt' || field === 'lastCheckedAt') {
      return formatDate(value as string | null | undefined);
    }

    if (field === 'checkType') {
      return certificate.checkType === 'url' ? 'URL Checked' : 'Manual';
    }

    if (field === 'csr') {
      return certificate.csr ? 'Yes' : 'No';
    }

    if (field === 'status') {
      return <Badge bg={getStatusVariant(certificate.status)}>{certificate.status || 'Unknown'}</Badge>;
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return value ?? '';
  };

  const renderSidebar = () => (
    <div className={`sidebar left ${sidebarOpen ? 'open' : 'closed'}`}>
      <Button variant="outline-primary" size="sm" onClick={() => setSidebarOpen((value) => !value)} className="toggle-btn">
        {sidebarOpen ? '<' : '>'}
      </Button>

      {sidebarOpen && (
        <div className="ssl-sidebar-actions">
          <h4>SSL Certificates</h4>
          <div className="d-grid gap-2">
            <Button
              variant={activeView === 'manage' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveView('manage')}
            >
              Manage Certificates List
            </Button>
            <Button
              variant={activeView === 'display' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveView('display')}
            >
              Display Certificates
            </Button>
            <Button
              variant={activeView === 'run' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveView('run')}
            >
              Run SSL Cert Expiry Check
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderManageView = () => (
    <>
      <h4>Manage Certificates List</h4>
      <Card>
        <Card.Header>
          <h5>{selectedCertificateId ? 'Edit Certificate' : 'Add Certificate'}</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Name</strong></Form.Label>
                <Form.Control value={formData.name} onChange={(event) => updateFormField('name', event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Environment</strong></Form.Label>
                <Form.Control value={formData.environment} onChange={(event) => updateFormField('environment', event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Certificate Type</strong></Form.Label>
                <Form.Select value={formData.checkType} onChange={(event) => updateFormField('checkType', event.target.value as 'url' | 'manual')}>
                  <option value="url">URL Checked</option>
                  <option value="manual">Manual</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><strong>URL</strong></Form.Label>
                <Form.Control value={formData.url} onChange={(event) => updateFormField('url', event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Common Name</strong></Form.Label>
                <Form.Control value={formData.commonName} onChange={(event) => updateFormField('commonName', event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Expiry Date</strong></Form.Label>
                <Form.Control
                  type="date"
                  value={toDateInputValue(formData.expiresAt)}
                  onChange={(event) => updateFormField('expiresAt', fromDateInputValue(event.target.value))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Issuer</Form.Label>
                <Form.Control value={formData.issuer} onChange={(event) => updateFormField('issuer', event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control value={formData.subject} onChange={(event) => updateFormField('subject', event.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Serial Number</Form.Label>
                <Form.Control value={formData.serialNumber} onChange={(event) => updateFormField('serialNumber', event.target.value)} />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Additional Notification Recipients</Form.Label>
                <Form.Control
                  value={formData.notificationEmails}
                  onChange={(event) => updateFormField('notificationEmails', event.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Notify Days Before</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={formData.notifyDaysBefore}
                  onChange={(event) => updateFormField('notifyDaysBefore', Number(event.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3 ssl-active-check">
                <Form.Check
                  checked={formData.isActive}
                  label="Active"
                  onChange={(event) => updateFormField('isActive', event.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>CSR</Form.Label>
            <Form.Control as="textarea" rows={4} value={formData.csr} onChange={(event) => updateFormField('csr', event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={formData.notes} onChange={(event) => updateFormField('notes', event.target.value)} />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button onClick={saveCertificate} disabled={loading}>{selectedCertificateId ? 'Update Certificate' : 'Add Certificate'}</Button>
            <Button variant="secondary" onClick={resetForm} disabled={loading}>New</Button>
          </div>
        </Card.Body>
      </Card>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Environment</th>
            <th>Common Name</th>
            <th>URL</th>
            <th>Expiry Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {certificates.map((certificate) => (
            <tr key={certificate.id}>
              <td>{certificate.environment}</td>
              <td>{certificate.commonName}</td>
              <td>{certificate.url}</td>
              <td>{formatDate(certificate.expiresAt)}</td>
              <td><Badge bg={getStatusVariant(certificate.status)}>{certificate.status || 'Unknown'}</Badge></td>
              <td>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => editCertificate(certificate)}>Edit</Button>
                  <Button size="sm" variant="outline-info" onClick={() => checkCertificateNow(certificate)} disabled={loading}>Check Now</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => deleteCertificate(certificate)} disabled={loading}>Delete</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );

  const renderDisplayView = () => (
    <>
      <h4>Display Certificates</h4>
      <Card>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search by Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchName}
                  onChange={(event: { target: { value: string } }) => setSearchName(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Environment</Form.Label>
                <Form.Select value={filterEnv} onChange={(event: { target: { value: string } }) => setFilterEnv(event.target.value)}>
                  <option value="">All Environments</option>
                  {uniqueEnvironments.map((env: string) => (
                    <option key={env} value={env}>{env}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select value={filterStatus} onChange={(event: { target: { value: string } }) => setFilterStatus(event.target.value)}>
                  <option value="">All Statuses</option>
                  <option value="Valid">Valid</option>
                  <option value="Expiring Soon">Expiring Soon</option>
                  <option value="Expired">Expired</option>
                  <option value="Check Failed">Check Failed</option>
                  <option value="Unknown">Unknown</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Sort by Expiry Date</Form.Label>
                <Form.Select value={sortDirection} onChange={(event) => setSortDirection(event.target.value as 'asc' | 'desc')}>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Label>Display Fields</Form.Label>
              <div className="field-picker">
                {displayFields.map((field) => (
                  <Form.Check
                    key={field.key}
                    inline
                    checked={visibleFields.includes(field.key)}
                    label={field.label}
                    onChange={() => toggleField(field.key)}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            {displayFields.filter((field) => visibleFields.includes(field.key)).map((field) => (
              <th key={field.key}>{field.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredCertificates.map((certificate: SSLCertificate) => (
            <tr key={certificate.id}>
              {displayFields.filter((field) => visibleFields.includes(field.key)).map((field) => (
                <td key={field.key}>{renderFieldValue(certificate, field.key)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );

  const renderRunView = () => (
    <>
      <h4>Run SSL Cert Expiry Check</h4>
      <Card>
        <Card.Body>
          <Button onClick={runExpiryCheck} disabled={loading}>
            {loading ? 'Running...' : 'Run SSL Cert Expiry Check'}
          </Button>
        </Card.Body>
      </Card>

      {runSummary && (
        <Card>
          <Card.Header>
            <h5>Latest Check Summary</h5>
          </Card.Header>
          <Card.Body>
            <Row className="summary-row">
              <Col>Checked: <strong>{runSummary.checked}</strong></Col>
              <Col>Successful: <strong>{runSummary.successful}</strong></Col>
              <Col>Failed: <strong>{runSummary.failed}</strong></Col>
              <Col>Manual: <strong>{runSummary.manual}</strong></Col>
              <Col>Expiring Soon: <strong>{runSummary.expiringSoon}</strong></Col>
              <Col>Expired: <strong>{runSummary.expired}</strong></Col>
              <Col>Emails Sent: <strong>{runSummary.emailsSent}</strong></Col>
            </Row>
            <Table striped bordered hover responsive className="mt-3">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Days Until Expiry</th>
                </tr>
              </thead>
              <tbody>
                {runSummary.results.map((result) => (
                  <tr key={result.id}>
                    <td>{result.name}</td>
                    <td>{result.status}</td>
                    <td>{result.message}</td>
                    <td>{result.daysUntilExpiry ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </>
  );

  return (
    <div className="ssl-certificates-container">
      <h1>SSL Certificates</h1>

      {statusMessage && (
        <Alert variant={statusType}>
          <strong>Status:</strong> {statusMessage}
        </Alert>
      )}

      <Row className="main-content-flex">
        {renderSidebar()}
        <div className="center-content">
          {activeView === 'manage' && renderManageView()}
          {activeView === 'display' && renderDisplayView()}
          {activeView === 'run' && renderRunView()}
        </div>
      </Row>
    </div>
  );
};

export default SSLCertificates;
